import { Field, useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, Pressable, View } from 'react-native';

import { AddMembersSection } from '@components/Common/AddMembersSection';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import TextField from '@components/shared/TextField/TextField';
import { FormValues } from '@components/Tasks/TaskFormStack';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import {
  TASK_DESC_LENGTH_LIMIT,
  TASK_NAME_LENGTH_LIMIT,
} from '@src/constants/constants';
import theme from '@themes/theme';

export const DESCRIPTION_LINE_HEIGHT = 24;
const DESCRIPTION_MIN_ROWS = 1;
// Auto-growth stops here: past 5 rows the field scrolls its own content rather
// than pushing the sheet's remaining fields (project, due date, members) out of
// reach on a phone (TA-10920: raised from 3).
export const DESCRIPTION_MAX_ROWS = 5;
const DESCRIPTION_MIN_HEIGHT = DESCRIPTION_MIN_ROWS * DESCRIPTION_LINE_HEIGHT;
const DESCRIPTION_MAX_HEIGHT = DESCRIPTION_MAX_ROWS * DESCRIPTION_LINE_HEIGHT;
// TextField pads multiline inputs by `spacing.s` at the top. That padding eats
// into a fixed height, so the input box has to be that much taller than the
// rows of text it is meant to show.
const DESCRIPTION_INPUT_PADDING_TOP = theme.spacing.s;

/**
 * Clamps a measured description content height to the range the field actually
 * renders — `DESCRIPTION_MIN_ROWS` rows when empty, growing one row at a time
 * up to `DESCRIPTION_MAX_ROWS`.
 *
 * The measuring Box below renders the description as-is (a single space when
 * empty), so a measurement is exactly the rows the text occupies: an empty or
 * one-line description measures one row, the default. TA-10920: it used to
 * append '\n' to keep a spare row ahead of the caret, which made the 1-row
 * default jump to 2 rows on the first re-measure (e.g. while typing the name).
 */
export const clampDescriptionHeight = (measured: number): number =>
  Math.min(DESCRIPTION_MAX_HEIGHT, Math.max(DESCRIPTION_MIN_HEIGHT, measured));

type TaskCreateFormModalProps = {
  textInputRef?: React.RefObject<TextInput>;
  taskname?: string;
  onFormValidityChange?: (isFormBlank: boolean) => void;
  isReadySubmitting: boolean;
  onDescriptionContentSizeChange?: (height: number) => void;
  /** Inline create-task error banner (TA-10210) */
  errorMsg?: string;
  onDismissError?: () => void;
} & ComponentProps<typeof AddMembersSection>;

export const TaskCreateModalFormNew: React.FC<TaskCreateFormModalProps> = ({
  taskname,
  onFormValidityChange,
  textInputRef,
  isReadySubmitting,
  onDescriptionContentSizeChange,
  errorMsg,
  onDismissError,
  ...addMembersSectionProps
}) => {
  const { t } = useTranslation();
  const {
    isVisible,
    projectTutorialState,
    setProjectTutorialStateAndInStorage,
  } = useProjectTutorial();

  const { values, errors } = useFormikContext<FormValues>();

  const { name } = values;

  const areAllFieldsBlank = () => {
    const { name, description, projectId, dueDate, files } = values;

    return (
      !name.trim() &&
      !description?.trim() &&
      !projectId &&
      !dueDate &&
      files?.length === 0 &&
      addMembersSectionProps.members?.length === 0
    );
  };

  const saveProjectTutorializationState = async (currentState: string) => {
    setProjectTutorialStateAndInStorage(currentState);
  };

  useEffect(() => {
    const isFormBlank = areAllFieldsBlank();
    onFormValidityChange?.(isFormBlank);
  }, [values, errors]);

  const { description } = values;

  useEffect(() => {
    if (projectTutorialState && isVisible) {
      if (description && description?.length > 0) {
        if (Number(projectTutorialState) > 11) {
          saveProjectTutorializationState(projectTutorialState?.toString());
          return;
        }
      }
    }
  }, [description?.length, projectTutorialState, isVisible]);

  const handleStoppedTyping = useCallback(
    debounce(() => {
      saveProjectTutorializationState('12');
    }, 800),
    []
  );

  const descriptionInputRef = React.useRef<TextInput>(null);
  // Auto-grows with content (TA-10495 #16) — driven by the same hidden
  // measuring Box below that already reports content height to the parent
  // sheet for its own snap-point logic; this just also uses it to size the
  // visible field instead of leaving it at a fixed 200px block.
  const [descriptionHeight, setDescriptionHeight] = useState(
    DESCRIPTION_MIN_HEIGHT
  );

  const getTaskname = () => {
    if (taskname === undefined) return null;
    return taskname;
  };

  return (
    <Box flex={1}>
      <View style={{ flex: 1 }}>
        <Box flex={1}>
          {!!errorMsg && (
            <Box
              flexDirection='row'
              alignItems='center'
              marginHorizontal='m'
              marginBottom='s'
              padding='s'
              borderRadius='xs'
              backgroundColor='grey01'
              accessibilityLabel='Task creation error'>
              <Icon variant='l' name='Retry' color='alertRed' />
              <Text
                flex={1}
                marginLeft='xs'
                variant='metadata'
                color='alertRed'>
                {errorMsg}
              </Text>
              <Pressable
                onPress={onDismissError}
                hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}
                accessibilityLabel='Dismiss error'>
                <Icon name='X2' variant='m' color='alertRed' />
              </Pressable>
            </Box>
          )}
          <Box paddingHorizontal='m'>
            <Field
              component={TextField}
              label='Name'
              name='name'
              accessibilityLabel='Name'
              isRequired
              placeholder={t('models:tasks.placeholders.nameShortModal')}
              borderless
              isCreateTask
              autoFocus
              showCharsRemaining={name?.length > 0}
              editable={!isReadySubmitting}
              // Anything outside `style` is spread onto the RN TextInput as a
              // prop, so style-shaped values (fontFamily/fontSize/color) have
              // to go in `style` to have any effect — see the description
              // field below.
              textInputProps={{
                maxLength: TASK_NAME_LENGTH_LIMIT,
                ref: textInputRef,
                multiline: false,
                numberOfLines: 1,
                scrollEnabled: true,
              }}
              defaultValue={getTaskname()}
            />
            {/* Description Field. Sizes to its own content (label + the
                clamped input) rather than stretching to fill the sheet — a
                `flex: 1` here is what previously turned the 2-row default into
                a full-height block.

                `marginBottom='m'` is the 16pt gap to whatever renders next —
                the sheet's footer (attachments preview + controls), which the
                field otherwise sat flush against once it grew (TA-10495 QA
                round 2). Onboarding's first task keeps it tight to match the
                Tutorialization design, since its footer is only the project
                row (TA-10920 #11). */}
            <Box
              marginBottom={values.isOnboardingTask ? 'xs' : 'm'}
              style={{ minHeight: descriptionHeight }}>
              <Pressable
                style={{ flex: 1 }}
                onPress={() => {
                  descriptionInputRef.current?.focus();
                }}>
                <Field name='description'>
                  {({ field, form, ...props }: any) => (
                    <TextField
                      {...props}
                      field={field}
                      form={form}
                      onChangeText={() => {
                        handleStoppedTyping();
                      }}
                      label='Description'
                      borderless
                      isCreateTask
                      showCharsRemaining
                      editable={!isReadySubmitting}
                      ref={descriptionInputRef}
                      textInputProps={{
                        maxLength: TASK_DESC_LENGTH_LIMIT,
                        multiline: true,
                        numberOfLines: DESCRIPTION_MIN_ROWS,
                        // Once the height caps at DESCRIPTION_MAX_ROWS the
                        // content has to scroll inside the field instead.
                        scrollEnabled: true,
                        style: {
                          // TextField lays the input out inside a
                          // `flexDirection: 'row'` Box, so `flex` here is the
                          // input's WIDTH, not its height — it has to stay 1
                          // or the field collapses to zero width and typed
                          // text is invisible (only the remaining-chars
                          // counter moves). The height is the explicit,
                          // clamped one below; nothing stretches it now that
                          // the wrapper Box no longer flexes.
                          flex: 1,
                          height:
                            descriptionHeight + DESCRIPTION_INPUT_PADDING_TOP,
                          // The colour TextField would otherwise apply lives in
                          // its `!borderless` branch, and everything in
                          // textInputProps outside `style` is spread as a raw
                          // TextInput prop (where RN ignores `color`) — so the
                          // colour has to be set here for the disabled
                          // (submitting) state to read as grey.
                          color: isReadySubmitting
                            ? theme.colors.grey04
                            : theme.colors.textPrimary,
                          textAlignVertical: 'top',
                        },
                      }}
                      placeholder={t(
                        'models:tasks.placeholders.descriptionShortModalNew'
                      )}
                      accessibilityLabel='Description'
                    />
                  )}
                </Field>
              </Pressable>
            </Box>
            <Box
              position='absolute'
              opacity={0}
              pointerEvents='none'
              // Layout measurement only — there is nothing here for a user to
              // hear. opacity/pointerEvents keep it off the screen and out of
              // the touch tree, but neither takes it out of the accessibility
              // tree, so VoiceOver/TalkBack would read the description back a
              // second time. Both platforms need their own opt-out.
              accessibilityElementsHidden
              importantForAccessibility='no-hide-descendants'
              style={{
                left: theme.spacing.m,
                right: theme.spacing.m,
                bottom: theme.spacing.s,
              }}
              onLayout={(e) => {
                const measured = e.nativeEvent.layout.height;
                // Clamped before it reaches the parent sheet too: the sheet
                // only has to make room for what the field actually shows, and
                // past DESCRIPTION_MAX_ROWS the field scrolls internally
                // instead of getting taller.
                const next = clampDescriptionHeight(measured);
                onDescriptionContentSizeChange?.(next);
                // The hidden measuring box re-emits layout on every keystroke,
                // and most of those report the same height (typing within a
                // line, or any keystroke once the clamp has capped out).
                // Return `prev` unchanged in that case so React bails out
                // instead of scheduling a render per character.
                setDescriptionHeight((prev) => (prev === next ? prev : next));
              }}>
              <Text>
                {values.description?.length ? values.description : ' '}
              </Text>
            </Box>
          </Box>
        </Box>
      </View>
    </Box>
  );
};
